import React, { Component } from 'react';
import { Modal, Button, Row, Col, Select } from 'antd';
import ActionButton from '$app/apps/bookings/components/ActionButton';
import { EditableContext } from '$app/components/vendor/WorkOperationsTable';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import sortBy from 'lodash/sortBy';
import WorkOperationsTable from '../WorkDetailsTable/WorkOperationsTable';
import { applyVat } from '$app/utils/util';
import { DeleteButton, DeleteIcon } from './DeleteButton';
import { ADJUSTMENT_TYPE, ADJUSTMENT_TITLE, ACP_205, ACP_307, ACP_308 } from '../../constants';

const inputOptions = {
  number: {
    type: 'number',
    // max: 0,
    onChange: () => {},
  },
  text: {
    type: 'text',
    onChange: () => {},
  },
};

const validator = (form, columnKey) => (rule, value, callback) => {
  const errors = [];
  if (columnKey === 'price' && typeof value !== 'number') {
    errors.push(new Error(`required`));
  }
  // if (columnKey === 'price' && value === 0) {
  //   errors.push(new Error(`negative amount required`));
  // }
  return callback(errors);
};

const rowTypes = {
  adjustment: ADJUSTMENT_TYPE,
  totals: 'totals',
};

const totalRowKey = 'total-row';
const addAdjustmentRow = 'add-adjustment-row';

const getRandomKey = () => Math.round(Math.random() * 10000);
const renderPrice = (price) => {
  if (price < 0) return <span> -£{Math.abs(price.toFixed(2))}</span>;
  return <span> £{price.toFixed(2)}</span>;
};

export default class AdjustmentOperationModal extends Component {
  constructor(props) {
    super(props);
    const editableAdjustment = props.editableAdjustment || {};

    this.state = {
      // Modal state
      isNew: !editableAdjustment.workSummary,
      editingKey: null,
      disableDeleteBtn: false,
      showEditDescription: (editableAdjustment || {}).workSummary || false,
      isSaving: false,
      isDeleting: false,
      // Work operation state
      workSummary: (editableAdjustment || {}).workSummary,
      type: ADJUSTMENT_TYPE,
      fixedCostWithVAT: 0,
      garageCostWithVat: (editableAdjustment || {}).garageCostWithVat || 0,
      subLineItems: (editableAdjustment || {}).subLineItems || [],
      order: editableAdjustment.order || 0,
    };
  }

  componentDidUpdate() {
    this.updateSubLineItems();
  }

  componentDidMount() {
    this.updateSubLineItems();
    if (this.state.isNew) this.addAdjustmentOperation();
  }

  get columns() {
    return [
      {
        title: '',
        key: 'workSummary',
        editable: ({ key }) => key !== totalRowKey,
        input: inputOptions.text,
        width: '14%',
      },
      {
        title: 'Garage',
        key: 'garage-group',
        children: [
          {
            title: 'Accounting Code',
            key: 'accountCodePurchases',
            editable: ({ key }) => key !== totalRowKey,
            input: inputOptions.text,
            align: 'right',
            width: '20%',
            render: (_, record) => {
              if (record.key !== record.operationId || record.key === totalRowKey) return null;
              return (
                <Select
                  style={{ width: '100%' }}
                  defaultValue={record.accountCodePurchases || 205}
                  onChange={(value) => this.setACP(record.key, value)}
                >
                  <Select.Option value={ACP_205}>205 - Car Part Sales</Select.Option>
                  <Select.Option value={ACP_307}>307 - Garage Penalties No VAT</Select.Option>
                  <Select.Option value={ACP_308}>308 - Garage Penalties Incl. VAT</Select.Option>
                </Select>
              );
            },
          },
          {
            title: 'Total (no VAT)',
            key: 'garageCost',
            editable: ({ key }) => key !== totalRowKey,
            input: inputOptions.number,
            displayPrice: true,
            align: 'right',
            width: '10%',
          },
          {
            title: 'Total (incl VAT)',
            key: 'garageCostWithVat',
            editable: ({ key }) => key !== totalRowKey,
            input: inputOptions.number,
            displayPrice: true,
            align: 'right',
            width: '10%',
            render: (_, record) => {
              if (record.key !== record.operationId) return null;
              return renderPrice(record.garageCostWithVat);
            },
          },
          {
            title: '',
            key: 'save',
            width: '6%',
            render: (val, record) => (
              <EditableContext>
                {({ form }) => {
                  if (record.key === this.state.editingKey) {
                    this.updateSubLineItems(record.key, form.getFieldsValue());
                  }
                  if (record.key === totalRowKey) {
                    return null;
                  }
                  if (record.key !== this.state.editingKey) {
                    return null;
                  }
                  return (
                    <Button
                      onClick={() => this.deleteWorkOperation(record.key)}
                      style={{
                        border: 'none',
                        padding: '0 8px',
                        float: 'right',
                      }}
                    >
                      <DeleteIcon />
                    </Button>
                  );
                }}
              </EditableContext>
            ),
          },
        ],
      },
    ];
  }

  setACP(key, accountCodePurchases) {
    const { subLineItems } = this.state;

    const newItem = subLineItems.find((i) => i.key === key);
    const updates = {
      ...newItem,
      accountCodePurchases,
      vatExempt: accountCodePurchases === ACP_307,
    };

    const newItems = subLineItems.map((i) => {
      if (i.key === key) return updates;
      return i;
    });

    this.setState((st) => ({
      ...st,
      subLineItems: newItems,
    }));
  }

  updateSubLineItems(key, edits = {}) {
    const { subLineItems } = this.state;
    let updatedSubLineItems = cloneDeep(subLineItems);

    const row = updatedSubLineItems.find((i) => i.key === key);

    if (row && isString(edits.workSummary)) {
      row.workSummary = edits.workSummary;
    }

    if (row && isNumber(edits.garageCost)) {
      row.garageCost = edits.garageCost;
      row.garageCostWithVat = applyVat(edits.garageCost);
      if (row.vatExempt) {
        row.garageCostWithVat = edits.garageCost;
      }
    }

    const updatedTotals = this.getTotals(updatedSubLineItems.filter((i) => i.key !== totalRowKey));
    updatedSubLineItems = [...updatedSubLineItems.filter((i) => i.key !== totalRowKey), updatedTotals];

    if (
      !isEqual(
        sortBy(subLineItems, (li) => li.order),
        sortBy(updatedSubLineItems, (li) => li.order)
      )
    ) {
      this.setState({
        subLineItems: updatedSubLineItems,
        fixedCostWithVAT: 0,
        garageCostWithVat: updatedTotals.garageCostWithVat,
      });
    }
  }

  deleteWorkOperation = (key) => {
    const { subLineItems } = this.state;
    const updatedSubLineItems = cloneDeep(subLineItems);
    const updatedTotals = this.getTotals(updatedSubLineItems.filter((i) => i.key !== key));
    this.setState({
      subLineItems: [
        ...updatedSubLineItems.filter((i) => i.key !== totalRowKey).filter((i) => i.key !== key),
        updatedTotals,
      ],
    });
  };

  getTotals = (subLineItems = []) => {
    const withTotals = subLineItems.reduce(
      (ac, item) => ({
        operationId: totalRowKey,
        order: ac.order,
        fixedCostWithVAT: 0,
        garageCostWithVat: ac.garageCostWithVat + item.garageCostWithVat,
        garageCost: ac.garageCost + item.garageCost,
        key: totalRowKey,
        workSummary: 'Total',
        type: rowTypes.totals,
        className: totalRowKey,
      }),
      {
        operationId: totalRowKey,
        order: subLineItems.length + 1,
        fixtedCostWithVat: 0,
        garageCostWithVat: 0,
        garageCost: 0,
        key: totalRowKey,
        workSummary: 'Total',
        type: rowTypes.totals,
        className: totalRowKey,
      }
    );

    return withTotals;
  };

  onSetEditRow = (editingKey) => {
    if (editingKey === addAdjustmentRow) {
      this.addAdjustmentOperation;
    } else {
      const { subLineItems } = this.state;
      this.setState({
        editingKey,
        /**
         * Set the activeEdit to this row key,
         * when new line items are pushed, we need to focus on them (See addAdjustmentOperation)
         * */
        subLineItems: subLineItems.map((i) => ({ ...i, activeEdit: editingKey === i.key })),
      });
    }
  };

  addAdjustmentOperation = () => {
    const key = getRandomKey();
    this.setState(
      {
        subLineItems: [
          ...this.state.subLineItems,
          {
            operationId: key,
            order: this.state.subLineItems.length + 1,
            garageCostWithVat: 0,
            garageCost: 0,
            fixtedCostWithVat: 0,
            key,
            workSummary: ADJUSTMENT_TITLE,
            type: rowTypes.adjustment,
            quantity: 0,
            price: 0,
            vatExempt: false,
            accountCode: '303',
            accountCodePurchases: ACP_205,
          },
        ],
        /**
         * Force row to be in editable state
         */
      },
      () => this.onSetEditRow(key)
    );
  };

  handleOnOk = () => {
    const children = this.state.subLineItems.filter((i) => i.key !== totalRowKey);
    const totals = this.getTotals(children);
    const adjustmentOperation = {
      workSummary: this.state.workSummary,
      type: this.state.type,
      fixedCost: 0,
      garageCostWithVat: totals.garageCostWithVat,
      garageCost: totals.garageCost,
      subLineItems: [...children],
      order: this.state.order,
    };

    this.props.onOk(adjustmentOperation);
  };

  handleOnDelete = (operationId) => {
    const { onDelete } = this.props;

    this.setState({
      disableDeleteBtn: true,
    });

    onDelete(operationId);
  };

  get addAdjustmentRow() {
    return {
      key: addAdjustmentRow,
      render: {
        workSummary: () => (
          <ActionButton icon="plus" onClick={this.addAdjustmentOperation}>
            Add an adjustment
          </ActionButton>
        ),
      },
    };
  }

  get dataSource() {
    const updatedSubLineItems = cloneDeep(this.state.subLineItems);
    const children = updatedSubLineItems
      .filter((i) => i.key !== totalRowKey)
      .sort((a, b) => a.order - b.order);
    return [
      {
        key: 'main-row',
        workSummary: 'Garage adjustment',
        editable: false,
        children: [...children, this.addAdjustmentRow],
      },
      this.getTotals(updatedSubLineItems.filter((i) => i.key === totalRowKey)),
    ];
  }

  get footer() {
    const { onCancel, editableAdjustment, isSaving, isDeleting } = this.props;
    if (this.state.isNew) {
      return [
        <Button key="back" onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={this.handleOnOk} loading={isSaving}>
          Add
        </Button>,
      ];
    }
    return [
      <DeleteButton
        onConfirm={() => this.handleOnDelete(editableAdjustment.operationId)}
        key="deleteButton"
        isDeleting={isDeleting}
        txt="Delete adjustment"
      />,
      <Button key="back" onClick={onCancel}>
        Cancel
      </Button>,
      <Button key="submit" type="primary" onClick={this.handleOnOk} loading={isSaving}>
        Save
      </Button>,
    ];
  }

  render() {
    const { visible = false, onCancel } = this.props;
    return (
      <Modal
        title={this.state.isNew ? 'Add an adjustment' : 'Edit adjustment'}
        visible={visible}
        onOk={this.handleOnOk}
        onCancel={onCancel}
        width={1000}
        footer={this.footer}
      >
        <Row style={{ marginBottom: 24, minHeight: 180 }}>
          <Col span={24}>
            <WorkOperationsTable
              key="WorkDetailsTableEditor"
              columns={this.columns}
              dataSource={this.dataSource}
              onSetEditRow={this.onSetEditRow}
              defaultExpandAllRows
              validator={validator}
            />
          </Col>
        </Row>
      </Modal>
    );
  }
}
